﻿import TKProductVariants from '@tk/components/tk.product.variants';
import { filterMessages } from '@tk/utilities/tk.messages';

const READY_TO_RUN_ATTR = 'data-tk-ready-to-run';

export default class KWProductVariants extends TKProductVariants {
    readyToRun?: boolean;
    variantToggler: NodeListOf<HTMLInputElement>;
    valueToHide: string;
    matchHeight?: boolean;
    matchingTile?: HTMLElement;

    static observedAttributes = [READY_TO_RUN_ATTR];

    constructor() {
        super();
        this.variantToggler = this.querySelectorAll('[data-tk-variant-toggler] input');
        this.valueToHide = this.getAttribute('data-tk-value-to-hide') || 'off';

        this.readyToRun = this.getAttribute(READY_TO_RUN_ATTR) === 'true';

        this.matchHeight = this.hasAttribute('data-tk-match-tile-height') || undefined;
        this.matchingTile = document.querySelector('[data-tk-matching-tile]') || undefined;
    }

    connectedCallback(): void {
        if (this.hasAttribute(READY_TO_RUN_ATTR) && !this.readyToRun) return;
        super.connectedCallback();
        this.initialize();
    }

    initialize() {
        if (this.variantToggler.length === 0) return;
        this.registerTogglerListener();

        if (this.matchHeight && this.matchingTile) this.setMaxHeight();
    }

    setMaxHeight():void {
        const tileHeight = this.matchingTile!.offsetHeight;
        this.style.maxHeight = `${tileHeight}px`;
    }

    attributeChangedCallback(name: string, oldValue: string, newValue: string) {
        if (name === READY_TO_RUN_ATTR && newValue === 'true') {
            super.connectedCallback();
            this.initialize();
        }
    }

    registerTogglerListener() {
        this.variantToggler.forEach((element) => {
            const onToggle = this.toggle.bind(this);
            this.pushListener({
                event: 'input',
                element,
                action: onToggle,
            });
        });
    }

    toggle(event: InputEvent) {
        const { value } = event.target as HTMLInputElement;
        if (value === this.valueToHide) {
            this.resetVisiblity();
            return;
        }
        const rows = this.querySelectorAll<HTMLElement>(`[data-tk-variant-toggler="${this.valueToHide}"]`);
        rows.forEach((row) => {
            row.hidden = true;
        });
    }

    resetVisiblity() {
        const rows = this.querySelectorAll<HTMLElement>('[data-tk-variant-toggler]');
        rows.forEach((row) => {
            row.hidden = false;
        });
    }

    protected prepareVariantFilters(): void {
        const filterAttribute = new Set<string>();

        this.productVariantsCollection.forEach((variant) => {
            Object.keys(variant.spec).forEach((key) => filterAttribute.add(key));
        });

        filterAttribute.forEach((attr: string) => {
            // filter undefined or null values
            const distinctOptions = new Set(
                this.productVariantsCollection.map((variant) => variant.spec[attr]).filter(Boolean),
            );
            const sortedOptions = new Set([...distinctOptions].sort());
            const selectElement = (
                this.querySelector<HTMLSelectElement>(`[data-tk-product-variant-filter="${attr}"]`) || undefined
            );

            this.filteredOptions.push({
                element: selectElement,
                attr,
                options: sortedOptions,
            });
        });

        this.filteredOptions.forEach((item) => {
            if (!item.element) return;
            item.element.disabled = item.options.size < 2;

            const optionsHTML = [...item.options].map(
                (option) => {
                    const urlParams = new URLSearchParams(window.location.search);
                    const isSelected = urlParams.get(item.attr) === option;
                    const escapedValue = option.replace(/"/g, '&quot;');
                    return `<option value="${escapedValue}" ${isSelected ? 'selected' : ''}>${option}</option>`;
                },
            ).join('');
            let resultHTML = '';

            item.options.size > 1
                ? resultHTML = `<option value="">${filterMessages.all.text}</option>${optionsHTML}`
                : resultHTML = optionsHTML;
            item.element.insertAdjacentHTML('beforeend', resultHTML);
        });

        this.enableFilterInURL && this.filterList();
    }
}